/**
 * Client-side .ics (iCalendar) generator for the public confirmation
 * screen. No server hit, no auth — works for guests who finished a
 * booking on the widget and want to drop it into Apple / Google /
 * Outlook calendar with one tap.
 *
 * iOS opens the file directly in Calendar.app; Android prompts
 * Google Calendar; desktop downloads an .ics file.
 */

export interface IcsBookingInput {
  restaurantName: string
  restaurantAddress?: string | null
  /** YYYY-MM-DD */
  date: string
  /** HH:MM[:SS] */
  time: string
  durationMinutes: number
  guests: number
  guestName?: string | null
  notes?: string | null
}

function pad(n: number): string { return String(n).padStart(2, '0') }

/** Format Date → UTC string like 20260530T193000Z, required by ICS. */
function toUtcStamp(d: Date): string {
  return d.getUTCFullYear() +
    pad(d.getUTCMonth() + 1) +
    pad(d.getUTCDate()) + 'T' +
    pad(d.getUTCHours()) +
    pad(d.getUTCMinutes()) +
    pad(d.getUTCSeconds()) + 'Z'
}

function escapeIcsText(s: string): string {
  // RFC 5545: escape backslash, comma, semicolon, newline.
  return s.replace(/\\/g, '\\\\').replace(/,/g, '\\,').replace(/;/g, '\\;').replace(/\r?\n/g, '\\n')
}

export function buildBookingIcs(b: IcsBookingInput): string {
  const [hh, mm] = b.time.split(':').map(Number)
  const start = new Date(b.date + 'T00:00:00')
  start.setHours(hh, mm, 0, 0)
  const end = new Date(start.getTime() + Math.max(15, b.durationMinutes) * 60_000)
  const now = new Date()

  const uid = `rezaro-${b.date.replace(/-/g, '')}-${pad(hh)}${pad(mm)}-${Math.random().toString(36).slice(2, 10)}@rezaro.de`

  const summary = `Tisch bei ${b.restaurantName}`
  const descBits: string[] = [
    `${b.guests} ${b.guests === 1 ? 'Gast' : 'Gäste'}`,
  ]
  if (b.guestName) descBits.push(`Auf den Namen ${b.guestName}`)
  if (b.notes) descBits.push(`Notiz: ${b.notes}`)
  descBits.push('', 'Reserviert über rezaro.de')
  const description = descBits.join('\n')

  const lines = [
    'BEGIN:VCALENDAR',
    'VERSION:2.0',
    'PRODID:-//Rezaro//Booking Widget//DE',
    'METHOD:PUBLISH',
    'BEGIN:VEVENT',
    `UID:${uid}`,
    `DTSTAMP:${toUtcStamp(now)}`,
    `DTSTART:${toUtcStamp(start)}`,
    `DTEND:${toUtcStamp(end)}`,
    `SUMMARY:${escapeIcsText(summary)}`,
    `DESCRIPTION:${escapeIcsText(description)}`,
    b.restaurantAddress ? `LOCATION:${escapeIcsText(b.restaurantAddress)}` : '',
    'STATUS:CONFIRMED',
    'BEGIN:VALARM',
    'ACTION:DISPLAY',
    'DESCRIPTION:Erinnerung — Reservierung',
    'TRIGGER:-PT2H',
    'END:VALARM',
    'END:VEVENT',
    'END:VCALENDAR',
  ].filter(Boolean)
  // RFC 5545 wants \r\n line endings.
  return lines.join('\r\n')
}

/**
 * Trigger a browser download of the .ics blob. On iOS Safari this
 * also opens Calendar.app directly.
 */
export function downloadIcs(filename: string, ics: string) {
  const blob = new Blob([ics], { type: 'text/calendar;charset=utf-8' })
  const url = URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = url
  a.download = filename.endsWith('.ics') ? filename : `${filename}.ics`
  document.body.appendChild(a)
  a.click()
  document.body.removeChild(a)
  // Defer revoke so Safari has time to read the blob.
  setTimeout(() => URL.revokeObjectURL(url), 5000)
}
